// frontend/src/pages/Checkout.jsx - COMPLETELY CLEANED
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { usePayment } from "../context/PaymentContext";
import { createOrder } from "../api";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faLock,
  faCreditCard,
  faMapMarkerAlt,
  faShoppingCart,
  faCheckCircle,
  faSpinner,
  faMobileAlt,
  faMoneyBillWave,
  faBuilding,
  faCalendarCheck,
  faBox,
  faGlobe,
  faShieldAlt,
  faPlus,
  faTrash,
  faCheck,
  faWallet,
  faUniversity,
  faExclamationTriangle,
  faPhone,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";

// ============================================
// PAYMENT METHODS CONFIG
// ============================================
const PAYMENT_METHODS_CONFIG = {
  cash_on_delivery: {
    label: "Cash on Delivery",
    icon: faMoneyBillWave,
    description: "Pay when your order arrives",
    requiresPhone: false,
  },
  bank_transfer: {
    label: "Bank Transfer",
    icon: faUniversity,
    description: "Pay via bank transfer",
    requiresPhone: false,
  },
  orange_money: {
    label: "Orange Money",
    icon: faMobileAlt,
    description: "Pay with Orange Money",
    requiresPhone: true,
  },
  afrimoney: {
    label: "Afrimoney",
    icon: faMobileAlt,
    description: "Pay with Afrimoney",
    requiresPhone: true,
  },
  wave: {
    label: "WAVE",
    icon: faMobileAlt,
    description: "Pay with WAVE",
    requiresPhone: true,
  },
  mobile_money: {
    label: "Mobile Money",
    icon: faMobileAlt,
    description: "Pay with Mobile Money",
    requiresPhone: true,
  },
  card: {
    label: "Card Payment",
    icon: faCreditCard,
    description: "Pay with debit/credit card",
    requiresPhone: false,
  },
};

// ============================================
// MAIN COMPONENT
// ============================================
const Checkout = () => {
  const { cart, loading: cartLoading, emptyCart } = useCart();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const {
    paymentMethods,
    loading: paymentLoading,
    fetchPaymentMethods,
    addPayment,
    removePayment,
    setDefault,
    getDefaultPaymentMethod,
    getPaymentMethodById,
    hasSavedPaymentMethods,
  } = usePayment();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [selectedSavedPayment, setSelectedSavedPayment] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const [formData, setFormData] = useState({
    shippingAddress: {
      recipientName: user?.name || "",
      street: user?.addresses?.[0]?.street || "",
      city: user?.addresses?.[0]?.city || "",
      district: user?.addresses?.[0]?.district || "",
      country: "Sierra Leone",
      phone: user?.phone || "",
    },
    paymentMethod: "cash_on_delivery",
    savedPaymentMethodId: "",
    mobileMoneyNumber: "",
    paymentReference: "",
    notes: "",
  });

  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: "card",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    nameOnCard: "",
    phoneNumber: "",
    isDefault: false,
  });

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    if (!cartLoading && (!cart?.items || cart.items.length === 0)) {
      navigate("/cart");
    }
  }, [cart, cartLoading, navigate]);

  useEffect(() => {
    const initializeCheckout = async () => {
      setLoadingInitial(true);
      if (user) {
        await fetchPaymentMethods();
        const defaultMethod = getDefaultPaymentMethod();
        if (defaultMethod) {
          setSelectedSavedPayment(defaultMethod);
          setFormData((prev) => ({
            ...prev,
            savedPaymentMethodId: defaultMethod._id,
          }));
        }
      }
      setLoadingInitial(false);
    };
    initializeCheckout();
  }, [user, fetchPaymentMethods, getDefaultPaymentMethod]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: { ...formData[parent], [child]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePaymentMethodChange = (method) => {
    setFormData((prev) => ({
      ...prev,
      paymentMethod: method,
      savedPaymentMethodId: "",
    }));
    setPaymentError("");
  };

  const handleSavedPaymentSelect = (methodId) => {
    const method = getPaymentMethodById(methodId);
    setSelectedSavedPayment(method);
    setFormData((prev) => ({
      ...prev,
      savedPaymentMethodId: methodId,
      paymentMethod: "saved_payment",
    }));
    setPaymentError("");
  };

  const handleNewPaymentChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setNewPaymentMethod({ ...newPaymentMethod, [name]: checked });
    } else {
      setNewPaymentMethod({ ...newPaymentMethod, [name]: value });
    }
  };

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    setPaymentError("");

    try {
      const paymentData = {
        type: newPaymentMethod.type,
        isDefault: newPaymentMethod.isDefault,
      };

      if (newPaymentMethod.type === "card") {
        if (
          !newPaymentMethod.cardNumber ||
          !newPaymentMethod.expiryDate ||
          !newPaymentMethod.cvv ||
          !newPaymentMethod.nameOnCard
        ) {
          setPaymentError("Please fill in all card details");
          return;
        }
        paymentData.cardNumber = newPaymentMethod.cardNumber.replace(/\s/g, "");
        paymentData.expiryDate = newPaymentMethod.expiryDate;
        paymentData.cvv = newPaymentMethod.cvv;
        paymentData.nameOnCard = newPaymentMethod.nameOnCard;
      } else {
        if (!newPaymentMethod.phoneNumber) {
          setPaymentError("Phone number is required for mobile money");
          return;
        }
        paymentData.phoneNumber = newPaymentMethod.phoneNumber;
      }

      const response = await addPayment(paymentData);
      if (response?.success) {
        setShowAddPayment(false);
        setNewPaymentMethod({
          type: "card",
          cardNumber: "",
          expiryDate: "",
          cvv: "",
          nameOnCard: "",
          phoneNumber: "",
          isDefault: false,
        });
        const newMethod = response.data;
        setSelectedSavedPayment(newMethod);
        setFormData((prev) => ({
          ...prev,
          paymentMethod: "saved_payment",
          savedPaymentMethodId: newMethod._id,
        }));
        setPaymentError("");
      }
    } catch (error) {
      setPaymentError(
        error.response?.data?.message || "Failed to add payment method",
      );
    }
  };

  const handleRemoveSavedPayment = async (methodId) => {
    if (
      window.confirm("Are you sure you want to remove this payment method?")
    ) {
      try {
        await removePayment(methodId);
        if (selectedSavedPayment?._id === methodId) {
          setSelectedSavedPayment(null);
          setFormData((prev) => ({
            ...prev,
            savedPaymentMethodId: "",
            paymentMethod: "cash_on_delivery",
          }));
        }
      } catch (error) {
        setPaymentError("Failed to remove payment method");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPaymentError("");

    if (
      formData.paymentMethod === "saved_payment" &&
      !formData.savedPaymentMethodId
    ) {
      setPaymentError("Please select a saved payment method");
      setSubmitting(false);
      return;
    }

    const mobileMethods = ["orange_money", "afrimoney", "wave", "mobile_money"];
    if (
      mobileMethods.includes(formData.paymentMethod) &&
      !formData.mobileMoneyNumber
    ) {
      setPaymentError(
        "Mobile money number is required for this payment method",
      );
      setSubmitting(false);
      return;
    }

    if (!cart?.items || cart.items.length === 0) {
      setPaymentError("Your cart is empty");
      setSubmitting(false);
      return;
    }

    const orderItems = cart.items.map((item) => ({
      productId: item.product?._id || item.productId,
      quantity: item.quantity,
    }));

    try {
      const orderData = {
        items: orderItems,
        shippingAddress: formData.shippingAddress,
        paymentMethod:
          formData.paymentMethod === "saved_payment"
            ? "saved_payment"
            : formData.paymentMethod,
        savedPaymentMethodId: formData.savedPaymentMethodId || null,
        mobileMoneyNumber: formData.mobileMoneyNumber || "",
        paymentReference: formData.paymentReference || "",
        notes: formData.notes,
        currency,
      };

      const response = await createOrder(orderData);
      const orderDataResult = response.data.data || response.data;
      setOrderId(orderDataResult._id);
      setOrderSuccess(true);
      await emptyCart();

      setTimeout(() => {
        navigate(`/orders/${orderDataResult._id}`);
      }, 3000);
    } catch (error) {
      console.error("Order creation error:", error);
      setPaymentError(
        error.response?.data?.message ||
          "Error creating order. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================
  // COMPUTED
  // ============================================
  if (cartLoading || loadingInitial) return <Loader />;
  if (!cart?.items || cart.items.length === 0) {
    navigate("/cart");
    return null;
  }

  const subtotalUSD =
    cart?.items?.reduce(
      (sum, item) => sum + (item.price?.usd || 0) * item.quantity,
      0,
    ) || 0;
  const totalQuantity =
    cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const isBulkOrder = totalQuantity >= 100;

  // ============================================
  // SUCCESS STATE
  // ============================================
  if (orderSuccess) {
    return (
      <motion.div
        className="checkout-success"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="checkout-success__icon">
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
        <h1>Order Placed Successfully!</h1>
        <p>
          Thank you for your order. We'll send you a confirmation email shortly.
        </p>
        <p className="checkout-success__order">
          Order #{orderId?.slice(-8) || "Processing"}
        </p>
        <div className="checkout-success__actions">
          <Button href={`/orders/${orderId}`} variant="primary">
            View Order Details
          </Button>
          <Button href="/products" variant="outline">
            Continue Shopping
          </Button>
        </div>
      </motion.div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  const hasSavedMethods = hasSavedPaymentMethods
    ? hasSavedPaymentMethods()
    : paymentMethods.length > 0;

  return (
    <motion.div
      className="checkout-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="checkout-hero">
        <div className="checkout-hero__overlay" />
        <div className="container checkout-hero__content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon
              icon={faShoppingCart}
              className="checkout-hero__icon"
            />
            <h1>Checkout</h1>
            <p>
              Review your order and complete your purchase from Kenali
              Enterprise
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Form */}
      <div className="container checkout-container">
        <Link to="/cart" className="checkout-back">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Cart
        </Link>

        {paymentError && (
          <div className="payment-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            {paymentError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="checkout-grid">
            {/* Left Column - Forms */}
            <div className="checkout-forms">
              {/* Shipping Address */}
              <motion.div
                className="checkout-section"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <h3>
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
                </h3>
                <div className="form-group">
                  <label>Recipient Name *</label>
                  <input
                    type="text"
                    name="shippingAddress.recipientName"
                    value={formData.shippingAddress.recipientName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="shippingAddress.street"
                    value={formData.shippingAddress.street}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="shippingAddress.city"
                      value={formData.shippingAddress.city}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>District *</label>
                    <input
                      type="text"
                      name="shippingAddress.district"
                      value={formData.shippingAddress.district}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="shippingAddress.phone"
                    value={formData.shippingAddress.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div
                className="checkout-section"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <h3>
                  <FontAwesomeIcon icon={faCreditCard} /> Payment Method
                </h3>

                {/* Saved Payment Methods */}
                {user && hasSavedMethods && (
                  <div className="saved-payment-methods">
                    <label className="payment-option-label">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="saved_payment"
                        checked={formData.paymentMethod === "saved_payment"}
                        onChange={() =>
                          handlePaymentMethodChange("saved_payment")
                        }
                      />
                      <FontAwesomeIcon icon={faWallet} /> Use Saved Payment
                      Method
                    </label>
                    {formData.paymentMethod === "saved_payment" && (
                      <div className="saved-methods-list">
                        {paymentMethods.map((method) => (
                          <label
                            key={method._id}
                            className={`saved-method ${formData.savedPaymentMethodId === method._id ? "selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name="savedPaymentMethodId"
                              value={method._id}
                              checked={
                                formData.savedPaymentMethodId === method._id
                              }
                              onChange={() =>
                                handleSavedPaymentSelect(method._id)
                              }
                            />
                            <FontAwesomeIcon
                              icon={
                                method.type === "card"
                                  ? faCreditCard
                                  : faMobileAlt
                              }
                            />
                            <span>
                              {method.type === "card"
                                ? `Card ending in ${method.last4}`
                                : method.phoneNumber || method.type}
                            </span>
                            {method.isDefault && (
                              <span className="default-badge">Default</span>
                            )}
                            <button
                              type="button"
                              className="remove-payment-btn"
                              onClick={() =>
                                handleRemoveSavedPayment(method._id)
                              }
                              aria-label="Remove payment method"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Payment Options */}
                <div className="payment-options">
                  {Object.entries(PAYMENT_METHODS_CONFIG).map(
                    ([key, config]) => {
                      const isMobile = [
                        "orange_money",
                        "afrimoney",
                        "wave",
                        "mobile_money",
                      ].includes(key);
                      return (
                        <label
                          key={key}
                          className={`payment-option ${formData.paymentMethod === key ? "selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={key}
                            checked={formData.paymentMethod === key}
                            onChange={() => handlePaymentMethodChange(key)}
                          />
                          <FontAwesomeIcon icon={config.icon} />
                          {config.label}
                          {isMobile && <span className="mobile-badge">📱</span>}
                        </label>
                      );
                    },
                  )}
                </div>

                {/* Mobile Money Number Field */}
                {["orange_money", "afrimoney", "wave", "mobile_money"].includes(
                  formData.paymentMethod,
                ) && (
                  <div className="form-group mobile-money-field">
                    <label>
                      <FontAwesomeIcon icon={faMobileAlt} /> Mobile Money Number
                      *
                    </label>
                    <input
                      type="tel"
                      name="mobileMoneyNumber"
                      value={formData.mobileMoneyNumber}
                      onChange={handleChange}
                      placeholder="+232 76 123456"
                      required
                    />
                    <small>
                      Enter the phone number registered with your mobile money
                      account
                    </small>
                  </div>
                )}

                {/* Payment Reference */}
                <div className="form-group">
                  <label>Payment Reference (Optional)</label>
                  <input
                    type="text"
                    name="paymentReference"
                    value={formData.paymentReference}
                    onChange={handleChange}
                    placeholder="Enter a reference for this payment"
                  />
                </div>

                {/* Add New Payment Method */}
                {user && (
                  <button
                    type="button"
                    className="add-payment-btn"
                    onClick={() => setShowAddPayment(!showAddPayment)}
                  >
                    <FontAwesomeIcon icon={faPlus} />{" "}
                    {showAddPayment ? "Cancel" : "Add Payment Method"}
                  </button>
                )}

                {showAddPayment && (
                  <div className="add-payment-form">
                    <h4>Add New Payment Method</h4>
                    <div className="form-group">
                      <label>Payment Type</label>
                      <select
                        name="type"
                        value={newPaymentMethod.type}
                        onChange={handleNewPaymentChange}
                      >
                        <option value="card">Card</option>
                        <option value="orange_money">Orange Money</option>
                        <option value="afrimoney">Afrimoney</option>
                        <option value="wave">WAVE</option>
                      </select>
                    </div>

                    {newPaymentMethod.type === "card" ? (
                      <>
                        <div className="form-group">
                          <label>Card Number</label>
                          <input
                            type="text"
                            name="cardNumber"
                            value={newPaymentMethod.cardNumber}
                            onChange={handleNewPaymentChange}
                            placeholder="1234 5678 9012 3456"
                          />
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Expiry Date</label>
                            <input
                              type="text"
                              name="expiryDate"
                              value={newPaymentMethod.expiryDate}
                              onChange={handleNewPaymentChange}
                              placeholder="MM/YY"
                            />
                          </div>
                          <div className="form-group">
                            <label>CVV</label>
                            <input
                              type="password"
                              name="cvv"
                              value={newPaymentMethod.cvv}
                              onChange={handleNewPaymentChange}
                              placeholder="123"
                              maxLength="4"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Name on Card</label>
                          <input
                            type="text"
                            name="nameOnCard"
                            value={newPaymentMethod.nameOnCard}
                            onChange={handleNewPaymentChange}
                            placeholder="John Doe"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={newPaymentMethod.phoneNumber}
                          onChange={handleNewPaymentChange}
                          placeholder="+232 76 123456"
                        />
                      </div>
                    )}

                    <div className="form-group checkbox">
                      <label>
                        <input
                          type="checkbox"
                          name="isDefault"
                          checked={newPaymentMethod.isDefault}
                          onChange={handleNewPaymentChange}
                        />
                        Set as default payment method
                      </label>
                    </div>

                    <button
                      type="button"
                      className="save-payment-btn"
                      onClick={handleAddPaymentMethod}
                    >
                      <FontAwesomeIcon icon={faCheck} /> Save Payment Method
                    </button>
                  </div>
                )}
              </motion.div>

              {/* Order Notes */}
              <motion.div
                className="checkout-section"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3>Order Notes (Optional)</h3>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Special delivery instructions or notes about your order..."
                />
              </motion.div>
            </div>

            {/* Right Column - Summary */}
            <motion.div
              className="checkout-summary"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h3>
                <FontAwesomeIcon icon={faShoppingCart} /> Order Summary
              </h3>

              {isBulkOrder && (
                <div className="bulk-badge">
                  🌾 Bulk Order ({totalQuantity}kg) - Priority Processing
                </div>
              )}

              <div className="summary-items">
                {cart.items.map((item) => (
                  <div key={item._id} className="summary-item">
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span>
                      <CurrencyDisplay
                        amountUSD={(item.price?.usd || 0) * item.quantity}
                        compact
                      />
                    </span>
                  </div>
                ))}
              </div>

              <hr className="summary-divider" />

              <div className="total-row">
                <span>Subtotal:</span>
                <span>
                  <CurrencyDisplay amountUSD={subtotalUSD} />
                </span>
              </div>
              <div className="total-row shipping">
                <span>Shipping:</span>
                <span>
                  {isBulkOrder || subtotalUSD >= 100
                    ? "Free"
                    : "Calculated at delivery"}
                </span>
              </div>

              <hr className="summary-divider" />

              <div className="total-row grand-total">
                <span>Total:</span>
                <span>
                  <CurrencyDisplay amountUSD={subtotalUSD} />
                </span>
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
                fullWidth
                size="large"
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Placing Order...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faLock} /> Place Order
                  </>
                )}
              </Button>

              <div className="summary-security">
                <FontAwesomeIcon icon={faLock} /> Your information is secure
              </div>

              <div className="summary-whatsapp">
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faWhatsappBrand} /> Need help? WhatsApp
                  us
                </a>
              </div>
            </motion.div>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default Checkout;
